import { DOCUMENT, inject, Injectable } from '@angular/core';

import { UrlFlagsService } from '../url-flags/url-flags.service';

const STORAGE_KEY_PREFIX = 'ocp.table-column-widths';

/**
 * Persists user-resized table column widths for the current browser session, so a table is
 * restored exactly as the user last left it when navigating away and back.
 *
 * Widths are stored as percentages (0-100) of the table's total width rather than raw pixels:
 * a table reopened at a different viewport/window size (or on a different monitor) still fills
 * it proportionally instead of being pinned to the exact pixel widths from a previous session,
 * which could otherwise overflow a narrower window or leave a wider one looking sparse.
 *
 * Widths are namespaced per `tableId` (e.g. `'tasks-list'`) and per embedded/standalone mode,
 * since the embedded (iframe/host-integrated) and standalone apps are used independently and a
 * layout tuned for one shouldn't dictate the other's column widths.
 *
 * Backed by `sessionStorage` (cleared when the tab/session ends) behind a small storage
 * abstraction — swapping to `localStorage` for cross-session persistence later only requires
 * changing the `storage` getter below.
 */
@Injectable({ providedIn: 'root' })
export class TableColumnWidthsStore {
  private readonly document = inject(DOCUMENT);
  private readonly urlFlags = inject(UrlFlagsService);

  /** In-memory mirror of storage, keyed by the same key used in `sessionStorage`. */
  private readonly cache = new Map<string, Record<string, number>>();

  /**
   * Returns the persisted column widths (`%`, keyed by column field) for the given table under
   * the current embedded/standalone mode. Returns an empty object if nothing is stored yet.
   */
  getWidths(tableId: string): Record<string, number> {
    const key = this.storageKey(tableId);
    const cached = this.cache.get(key);
    if (cached) return cached;

    const widths = this.readFromStorage(key);
    this.cache.set(key, widths);
    return widths;
  }

  /** Persists the full set of column widths (`%`), replacing any previously stored for that table. */
  setWidths(tableId: string, widthPercentages: Record<string, number>): void {
    const key = this.storageKey(tableId);
    this.cache.set(key, widthPercentages);
    this.writeToStorage(key, widthPercentages);
  }

  /** Clears persisted widths for the given table under the current mode only. */
  clearWidths(tableId: string): void {
    const key = this.storageKey(tableId);
    this.cache.delete(key);
    this.storage?.removeItem(key);
  }

  private storageKey(tableId: string): string {
    const mode = this.urlFlags.embedded() ? 'embedded' : 'standalone';
    return `${STORAGE_KEY_PREFIX}.${mode}.${tableId}`;
  }

  private get storage(): Storage | null {
    try {
      return this.document.defaultView?.sessionStorage ?? null;
    } catch {
      // Storage access can throw in some sandboxed/embedded iframe contexts.
      return null;
    }
  }

  private readFromStorage(key: string): Record<string, number> {
    try {
      const raw = this.storage?.getItem(key);
      if (!raw) return {};
      const parsed: unknown = JSON.parse(raw);
      return this.isWidthsRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeToStorage(key: string, widths: Record<string, number>): void {
    try {
      this.storage?.setItem(key, JSON.stringify(widths));
    } catch {
      // Storage may be unavailable (private browsing, quota, embedded restrictions) — widths
      // still work for the remainder of the page lifetime via the in-memory cache above.
    }
  }

  private isWidthsRecord(value: unknown): value is Record<string, number> {
    return (
      !!value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.values(value).every(entry => typeof entry === 'number')
    );
  }
}
